import {
    WorkflowFormQuestion,
    getAutoNextQuestionId,
    getFirstQuestionId,
    getNextQuestionId,
} from '../../../../utilsComponents/workflowParser'

/**
 * Finds the next actual “question” (visible to the user) based on a
 * workflow node ID. Some nodes are not questions but intermediate steps
 * (calculations, redirects): this function follows the `trigger` chain
 * until it encounters a node that is part of `workflowQuestions`.
 *
 * @param fromId - ID of the starting node (can be a question or a technical node)
 * @param workflowQuestions - List of “actual” questions in the workflow
 * @param rawByIdMap - Map of all raw nodes in the workflow (questions + technical nodes)
 * @param visited - Internal set to prevent infinite loops (trigger cycles)
 * @returns The ID of the first actual question found, or null if none is reachable
 */
export function findNextRealQuestion(
    fromId: string,
    workflowQuestions: WorkflowFormQuestion[],
    rawByIdMap: Map<string, any>,
    visited: Set<string> = new Set(),
): string | null {
    if (visited.has(fromId)) return null
    visited.add(fromId)
    if (workflowQuestions.some((q) => q.id === fromId)) {
        return fromId
    }
    const node = rawByIdMap.get(fromId)
    if (node && typeof node.trigger === 'string') {
        return findNextRealQuestion(node.trigger, workflowQuestions, rawByIdMap, visited)
    }
    return null
}

// extracts the selected choice ID (or raw value) from a question's answers,
export const getSelectedChoiceId = (answers: any[] | undefined): string | null => {
    const firstVal = answers?.[0]
    if (typeof firstVal === 'string') return firstVal
    if (typeof firstVal === 'object' && firstVal !== null) {
        return firstVal.id ?? firstVal.value ?? firstVal.trigger ?? null
    }
    return null
}

// resolves the next question reached from `questionId` with the given answers
export const resolveNextQuestionId = (
    questionId: string,
    answers: any[] | undefined,
    workflowQuestions: WorkflowFormQuestion[],
    workflowJsonContent: any,
    rawByIdMap: Map<string, any>,
): string | null => {
    // no answer on an optional question = skipped
    const question = workflowQuestions.find((q) => q.id === questionId)
    if ((!answers || answers.length === 0) && question?.optionalTrigger) {
        const skippedNextId = findNextRealQuestion(question.optionalTrigger, workflowQuestions, rawByIdMap)
        if (skippedNextId) return skippedNextId
    }
    const selectedChoiceId = getSelectedChoiceId(answers)
    if (selectedChoiceId) {
        const triggeredId = getNextQuestionId(questionId, selectedChoiceId, workflowQuestions, workflowJsonContent)
        if (triggeredId) {
            const nextId = findNextRealQuestion(triggeredId, workflowQuestions, rawByIdMap)
            if (nextId) return nextId
        }
    }
    const currentIdx = workflowQuestions.findIndex((q) => q.id === questionId)
    if (currentIdx !== -1 && currentIdx + 1 < workflowQuestions.length) {
        return workflowQuestions[currentIdx + 1]?.id || null
    }
    return null
}

// resolves the question following an "auto" node
export const resolveAutoNextQuestionId = (
    questionId: string,
    workflowQuestions: WorkflowFormQuestion[],
    workflowJsonContent: any,
): string | null => {
    const autoNextId = getAutoNextQuestionId(questionId, workflowJsonContent)
    if (autoNextId && workflowQuestions.some((q) => q.id === autoNextId)) {
        return autoNextId
    }
    const currentIdx = workflowQuestions.findIndex((q) => q.id === questionId)
    if (currentIdx !== -1 && currentIdx + 1 < workflowQuestions.length) {
        return workflowQuestions[currentIdx + 1]?.id ?? null
    }
    return null
}

// rebuilds the path actually taken by the user by replaying committed answers from the first question.
// returns ordered question IDs
export const getAnsweredPath = (
    workflowQuestions: WorkflowFormQuestion[],
    committedAnswersByQuestionId: Map<string, any[]>,
    workflowJsonContent: any,
    rawByIdMap: Map<string, any>,
): string[] => {
    const path: string[] = []
    const visited = new Set<string>()
    let currentId: string | null = getFirstQuestionId(workflowQuestions, workflowJsonContent)
    while (currentId && !visited.has(currentId)) {
        visited.add(currentId)
        path.push(currentId)
        const question = workflowQuestions.find((q) => q.id === currentId)
        if (question?.auto) {
            currentId = resolveAutoNextQuestionId(currentId, workflowQuestions, workflowJsonContent)
        } else if (committedAnswersByQuestionId.has(currentId)) {
            currentId = resolveNextQuestionId(
                currentId,
                committedAnswersByQuestionId.get(currentId),
                workflowQuestions,
                workflowJsonContent,
                rawByIdMap,
            )
        } else {
            break
        }
    }
    return path
}

// finds the previous question displayed to the user
export const getPreviousVisibleQuestionId = (
    currentId: string,
    path: string[],
    workflowQuestions: WorkflowFormQuestion[],
): string | null => {
    const currentIdx = path.indexOf(currentId)
    const candidates = currentIdx === -1 ? path : path.slice(0, currentIdx)
    for (let idx = candidates.length - 1; idx >= 0; idx--) {
        const question = workflowQuestions.find((q) => q.id === candidates[idx])
        if (question && !question.auto && question.id !== currentId) return question.id ?? null
    }
    return null
}
